import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { isFPIPolicy, isMobile } from '../../services/utils';
import {
  CHANGE_COVERAGE,
  CHANGE_COVERAGE_SUBMIT_SUCCESS,
  CHANGE_COVERAGE_WEB,
} from '../navigation/routes.constants';
import {
  clearPolicyNotifications,
  getChangeCoveragePreview,
  getPrivateToken,
  petSelected,
  policySelected,
  restartRate,
  setSelectedPetId,
  setSelectedPetIndex,
} from '../../actions';
import { MobileHeader } from '../navigation/MobileHeader';
import {
  useAlphabeticalSortedPetsWithPolicy,
  useDirectCancel,
  usePetcloudCCProp103,
  usePostMessage,
  useQuery,
  useSuccessScreenChangeCoverage,
} from '../../hooks';
import { usePetPolicies } from '../../hooks/usePetPolicies';
import { SubmitSuccess } from './changeCoverageSubmitted/SubmitSuccess';
import {
  ChangeCoverageScreenPreview,
  ChangeCoverageScreenOptions,
  VETERINARY_EXAM_FEES_PSM_ID,
  WELLNESS_PSM_ID,
} from '../insurance/changeCoverage';
import { CancelRenewalSent }
  from '../insurance/changeCoverage/changeCoverageOptions/CancelRenewalSent';
import { CancelRenewalSuccess }
  from '../insurance/changeCoverage/changeCoverageOptions/CancelRenewalSuccess';
import { POST_MESSAGE_TYPES } from '../../constants';

import './ChangeCoverageScreen.css';
import { MARKETING_CHANNEL_ID } from '../../theme/theme.constants';
import { ChangeCoverageSubmitted } from '../insurance/changeCoverage/changeCoverageSubmit';

const ChangeCoverageScreen = () => {
  const { t } = useTranslation('changeCoverage');
  const match = useRouteMatch();
  const history = useHistory();
  const dispatch = useDispatch();
  const query = useQuery();
  const postMessage = usePostMessage();
  const showDirectCancel = useDirectCancel();
  const publicTokenParam = query.get('publicToken');

  const useCCProp103 = usePetcloudCCProp103();
  const ChangeCoverageSubmittedFlag = useSuccessScreenChangeCoverage();

  const [policyNumber, setPolicyNumber] = useState(match.params.policyNumber
    || '');
  const [publicToken, setPublicToken] = useState(publicTokenParam || '');
  const store = useSelector(({
    policies,
    session,
    settings,
  }) => ({ policies, session, settings }), shallowEqual);
  const {
    policies: {
      allPets,
      changeCoverage: {
        cancelMultibrandPolicy,
        cancelRenewalRequestSent,
        loadingPreview,
        preview,
        success,
      },
      loading: policiesLoading,
    },
    settings: { featureFlags },
  } = store;
  const sortedPetsWithPolicy =
    useAlphabeticalSortedPetsWithPolicy();
  const { hasPets } = usePetPolicies();

  const powerUps = useMemo(() => [
    {
      detail: preview.VeterinaryInformation,
      id: VETERINARY_EXAM_FEES_PSM_ID,
      included: preview.HaveVeterinaryFees,
      title: t('preview.vetFees'),
    },
    {
      detail: preview.WellnessInformation,
      id: WELLNESS_PSM_ID,
      included: preview.HaveWellness,
      title: t('preview.wellness'),
    },
  ], [preview]);

  function handlePolicySelect(index) {
    const { policies } = store;

    dispatch(clearPolicyNotifications());

    if (!policies.allPolicies.length) {
      return;
    }

    dispatch(policySelected(index));
  }

  function handlePetSelect(index) {
    const petId = allPets[index].PetId;
    const indexBillingPayment = sortedPetsWithPolicy
      .findIndex((pet) => pet.PetId === petId);
    const indexNotFound = -1;
    const firstIndex = 0;
    dispatch(petSelected(indexBillingPayment === indexNotFound
      ? firstIndex
      : indexBillingPayment));
    dispatch(setSelectedPetId(petId));
    dispatch(clearPolicyNotifications());
    dispatch(setSelectedPetIndex(index));
  }

  // WIP prop103: preview
  const previewData = useMemo(() => {
    // WIP prop103: only for testing
    const CoverageName = 'Accident Only';

    if (useCCProp103) {
      return {
        ...preview,
        CoverageName,
      };
    }

    return preview;
  }, [preview]);

  useEffect(() => {
    if (policiesLoading) {
      return;
    }

    if (hasPets && store.session.privateToken) {
      const petIndexToSelect = sortedPetsWithPolicy
        .findIndex((pet) => pet.policies
          .find((policy, index) => {
            const policyFound = policy.Number === policyNumber;
            if (policyFound) {
              handlePolicySelect(index);
            }
            return policyFound;
          }));

      if (petIndexToSelect >= 0) {
        handlePetSelect(petIndexToSelect);
      }
    }
  }, [
    dispatch,
    hasPets,
    policyNumber,
    sortedPetsWithPolicy,
    store.session.privateToken,
  ]);

  useEffect(() => () => {
    dispatch(restartRate());
  }, []);

  useEffect(() => {
    setPolicyNumber(match.params.policyNumber || '');
    if (!publicToken) {
      setPublicToken(publicTokenParam);
    }
  }, [match.params, publicTokenParam]);

  useEffect(() => {
    if (!isMobile()) {
      history.replace(`${CHANGE_COVERAGE_WEB}/${policyNumber}`);
    } else if (!publicToken) {
      history.replace('/');
    }
  }, [publicToken]);

  useEffect(() => {
    if (!store.session.privateToken && publicToken && !featureFlags.loading) {
      dispatch(getPrivateToken({ publicToken }));
    }
  }, [
    dispatch,
    featureFlags.loading,
    publicToken,
    store.session.privateToken,
  ]);

  useEffect(() => {
    if (store.session.privateToken && isFPIPolicy(policyNumber)) {
      dispatch(getChangeCoveragePreview({ policyNumber }));
    }
  }, [dispatch, policyNumber, store.session.privateToken]);

  useEffect(() => {
    if (success) {
      history.push(CHANGE_COVERAGE_SUBMIT_SUCCESS);
    }
  }, [history, success]);

  function handleChangeCoverageButton() {
    history.push(`${match.url}/options`);
  }

  function handleKeepCoverageButton() {
    postMessage(POST_MESSAGE_TYPES.CLOSE_PAGE);
  }

  function handleRateError() {
    history.goBack();
  }

  function shouldPromptWarning() {
    return false;
  }

  function renderContent() {
    if (!store.session.privateToken) {
      return null;
    }

    if (showDirectCancel
      && !cancelMultibrandPolicy.loading
      && cancelMultibrandPolicy.success
    ) {
      return (
        <CancelRenewalSuccess
          onReturnClick={handleKeepCoverageButton}
          renewalDate={preview?.NewPremiumEffectiveDate}
        />
      );
    }

    if (!showDirectCancel && cancelRenewalRequestSent) {
      return <CancelRenewalSent onReturnClick={handleKeepCoverageButton} />;
    }

    return (
      <>
        <Switch>
          <Route path={`${match.path}/options`}>
            <ChangeCoverageScreenOptions
              onRateCreationError={handleRateError}
              policyNumber={policyNumber}
              validatePromptWarning={shouldPromptWarning}
            />
          </Route>

          <Route path={CHANGE_COVERAGE_SUBMIT_SUCCESS}>
            {/* duplicated path for webview only (: */}
            {ChangeCoverageSubmittedFlag
              ? <ChangeCoverageSubmitted isWebView />
              : <SubmitSuccess onClose={handleKeepCoverageButton} />}
          </Route>

          <Route path={CHANGE_COVERAGE}>
            <ChangeCoverageScreenPreview
              loading={loadingPreview}
              marketingChannel={MARKETING_CHANNEL_ID.figo}
              onChangeCoverageClick={handleChangeCoverageButton}
              onKeepClick={handleKeepCoverageButton}
              policyNumber={policyNumber}
              powerUps={powerUps}
              preview={previewData}
            />
          </Route>

        </Switch>
      </>
    );
  }

  return (
    <div
      className={`Change-coverage-main-container${cancelRenewalRequestSent
        || cancelMultibrandPolicy.success
        ? '-request-sent' : ''}`}
    >
      <MobileHeader />

      {renderContent()}
    </div>
  );
};

export { ChangeCoverageScreen };
